import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cn } from "@beweegbaas/ui/lib/utils";

const badgeVariants = cva(
  [
    "gap-1.5 rounded-full border border-transparent font-bold inline-flex items-center justify-center text-nowrap transition-colors duration-200 ease-in-out has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&>svg]:pointer-events-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-colors overflow-hidden group/badge w-fit shrink-0",
    "squircle supports-squircle:rounded-full",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-blue-500/50 text-primary-foreground dark:text-foreground blue:text-white! blue:bg-blue-900/50!",
        muted: "bg-muted text-muted-foreground",
        secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        destructive:
          "bg-destructive/10 [a]:hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 text-destructive dark:bg-destructive/20",
        outline: "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
        green:
          "bg-(--tint-green-bg) text-(--tint-green-fg) blue:text-green-100 blue:bg-green-300/50",
        yellow: "bg-(--tint-yellow-bg) text-(--tint-yellow-fg)",
        gray: "bg-(--tint-gray-bg) text-(--tint-gray-fg)",
        red: "bg-(--tint-red-bg) text-(--tint-red-fg)",
        purple: "bg-(--tint-purple-bg) text-(--tint-purple-fg)",
        indigo: "bg-(--tint-indigo-bg) text-(--tint-indigo-fg)",
        orange: "bg-(--tint-orange-bg) text-(--tint-orange-fg)",
        blue: "bg-(--tint-blue-bg) text-(--tint-blue-fg)",
        teal: "bg-teal-50 text-teal-600",
        fuchsia: "bg-fuchsia-50 text-fuchsia-600",
        pink: "bg-pink-100 text-pink-700",
        cyan: "bg-cyan-100 text-cyan-700",
        darkGray: "bg-gray-200 text-gray-800",
        white: "bg-white text-black shadow",
        transparent: "bg-transparent text-black dark:text-white",
        "opaque-white": "bg-white/10 text-white",
      },
      size: {
        sm: "px-2 py-0.5 text-xs [&>svg]:size-3! h-5",
        md: "px-3 py-0.5 text-xs [&>svg]:size-3! h-5",
        lg: "px-4 py-0.5 text-base [&>svg]:size-4! h-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

function Badge({
  className,
  variant,
  size = "md",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant, size }), focusStyles, className),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
      size,
    },
  });
}

export { Badge, badgeVariants };
